/** Extract opdrachtgever / eindklant / legacy client label from a vacancy row. */
export function companyNameFromVacancyRecord(vac: unknown): string {
    const v = vac as Record<string, unknown> | null | undefined;
    if (!v) return '';
    const data = (v.data as Record<string, unknown> | undefined) || undefined;
    const og = v.opdrachtgever as { name?: string } | undefined;
    const ek = v.eindklant as { name?: string } | undefined;
    const cl = v.clients as { name?: string } | undefined;
    return (
        String(og?.name || '').trim() ||
        String(ek?.name || '').trim() ||
        String(cl?.name || '').trim() ||
        String(data?.clientName || '').trim() ||
        String(data?.opdrachtgever || '').trim() ||
        String(data?.organizationText || '').trim() ||
        ''
    );
}

export function vacancyTitleFromVacancyRecord(vac: unknown): string {
    const v = vac as Record<string, unknown> | null | undefined;
    if (!v) return '';
    const data = (v.data as Record<string, unknown> | undefined) || undefined;
    return String(v.title || data?.title || data?.vacancyTitle || '').trim();
}

export type CandidateWhatsAppVacancyContext = {
    vacancyTitle: string;
    companyName: string;
    vacancyData: unknown;
};

/** Context for WhatsApp template / bulk message shortcode replacement. */
export type WhatsAppPersonalizeContext = {
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phone: string;
    vacancyTitle: string;
    companyName: string;
};

export type WhatsAppCandidateLike = {
    name?: string;
    email?: string;
    phone?: string;
    vacancyTitle?: string;
    companyName?: string;
    role?: string;
};

/** Resolve display values from candidate row + optional vacancy context. */
export function buildWhatsAppPersonalizeContext(
    candidate: WhatsAppCandidateLike,
    vacancyData?: unknown
): WhatsAppPersonalizeContext {
    const vac = vacancyData as Record<string, unknown> | null | undefined;
    const vacData = (vac?.data as Record<string, unknown> | undefined) || undefined;
    const vacNested = vac?.vacancies as Record<string, unknown> | undefined;

    const fullName = String(candidate.name || '').trim();
    const parts = fullName.split(/\s+/).filter(Boolean);
    const firstName = parts[0] || 'Kandidaat';
    const lastName = parts.slice(1).join(' ');

    const vacancyFromRecord = vacancyTitleFromVacancyRecord(vac);
    const companyFromRecord = companyNameFromVacancyRecord(vac);

    const vacancyFallbackTitle =
        vacancyFromRecord ||
        String(vac?.vacancyTitle || '').trim() ||
        String(vacNested?.title || '').trim() ||
        String(vacData?.title || '').trim() ||
        String(vacData?.vacancyTitle || '').trim() ||
        '';

    const companyFallbackName =
        String(candidate.companyName || '').trim() ||
        companyFromRecord ||
        String(vac?.companyName || '').trim() ||
        String(vac?.clientName || '').trim() ||
        String((vac?.client as { name?: string } | undefined)?.name || '').trim() ||
        String((vac?.clients as { name?: string } | undefined)?.name || '').trim() ||
        String((vac?.source_client as { name?: string } | undefined)?.name || '').trim() ||
        String(vacData?.clientName || '').trim() ||
        '';

    const vacancyTitle =
        String(candidate.vacancyTitle || '').trim() ||
        vacancyFallbackTitle ||
        String(candidate.role || '').trim() ||
        'deze functie';

    const companyName =
        companyFallbackName || 'ons bedrijf';

    return {
        firstName,
        lastName,
        fullName: fullName || firstName,
        email: String(candidate.email || '').trim(),
        phone: String(candidate.phone || '').trim(),
        vacancyTitle,
        companyName,
    };
}

/**
 * Replace WhatsApp shortcodes (bracket + mustache variants, any casing).
 * Aligns with CandidateMoveModal / BulkEmailComposerModal patterns.
 */
export function personalizeWhatsAppBody(body: string, ctx: WhatsAppPersonalizeContext): string {
    if (!body) return '';

    return (
        body
            // Naam / voornaam
            .replace(
                /\[voornaam\]|\[naam\]|\[name\]|\{\{\s*(?:voornaam|naam|name|candidate\.?first_name)\s*\}\}/gi,
                ctx.firstName
            )
            .replace(/\[Voornaam\]|\[Naam\]|\[Name\]/g, ctx.firstName)
            // Achternaam
            .replace(/\[achternaam\]|\{\{\s*(?:achternaam|candidate\.?last_name)\s*\}\}/gi, ctx.lastName)
            .replace(/\[Achternaam\]/g, ctx.lastName)
            // Volledige naam
            .replace(
                /\[volledige\s*naam\]|\[volledigenaam\]|\{\{\s*(?:candidate\.?name|full_name)\s*\}\}/gi,
                ctx.fullName
            )
            // Vacature / functie
            .replace(
                /\[vacature\]|\[functie\]|\{\{\s*(?:vacature|functie|vacancy\.?title|job\.?title)\s*\}\}/gi,
                ctx.vacancyTitle
            )
            .replace(/\[Vacature\]|\[Functie\]/g, ctx.vacancyTitle)
            // Bedrijf / opdrachtgever
            .replace(
                /\[opdrachtgever\]|\[bedrijf\]|\[bedrij\]|\[organisatie\]|\{\{\s*(?:bedrijf|opdrachtgever|company|client\.?name)\s*\}\}/gi,
                ctx.companyName
            )
            .replace(/\[Bedrijf\]|\[Bedrij\]|\[Opdrachtgever\]/g, ctx.companyName)
            // Contact
            .replace(
                /\[telefoonnummer\]|\[telefoon\]|\[phone\]|\{\{\s*(?:telefoon|phone|candidate\.?phone)\s*\}\}/gi,
                ctx.phone
            )
            .replace(/\[Telefoonnummer\]|\[Telefoon\]/g, ctx.phone)
            .replace(/\[email\]|\[e-mail\]|\{\{\s*(?:email|candidate\.?email)\s*\}\}/gi, ctx.email)
            .replace(/\[Email\]/g, ctx.email)
    );
}
